import { useEffect, useState } from "react";
import { ChevronDown, ExternalLink, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { buildShopUrls, trackShopClick } from "@/lib/myntra";

/**
 * StyleGapSection — "Complete your look" cards under an outfit suggestion.
 * Props:
 *   suggestionId  string  saved suggestion id (POST /api/gaps/analyze pulls context from this)
 *   gender        string  "women" | "men" — used to bias Myntra search
 *   occasion      string  fallback if suggestion is unsaved
 *   vibe          string  fallback
 */
export default function StyleGapSection({ suggestionId, gender, occasion, vibe }) {
  const [state, setState] = useState({ loading: true, gaps: [], config: null, error: null });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/gaps/analyze", {
          suggestion_id: suggestionId,
          occasion,
          vibe,
        });
        if (!cancelled) {
          setState({ loading: false, gaps: data.gaps || [], config: data.config || null, error: null });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, gaps: [], config: null, error: "Could not load suggestions" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [suggestionId, occasion, vibe]);

  if (state.loading) {
    return (
      <section className="mt-10" data-testid="style-gaps-loading">
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-3">
          Complete your look
        </div>
        <div className="text-sm text-stone-400 font-[Manrope] italic">Scanning your wardrobe…</div>
      </section>
    );
  }

  if (state.error) {
    return null; // fail-quiet: outfit is still usable without gaps
  }

  if (state.gaps.length === 0) {
    return (
      <section className="mt-10" data-testid="style-gaps-empty">
        <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-3">
          Complete your look
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white/60 backdrop-blur-sm p-4">
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="text-[#6B7A54] mt-0.5 shrink-0" />
            <div className="text-sm font-[Manrope] text-stone-700 leading-relaxed">
              Your wardrobe&rsquo;s got this covered <span aria-hidden>✨</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mt-10" data-testid="style-gaps-section">
      <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-3">
        Complete your look
      </div>
      <div className="flex flex-col gap-3">
        {state.gaps.map((gap, idx) => (
          <GapCard
            key={`${gap.category}-${idx}`}
            gap={gap}
            gender={gender}
            config={state.config}
            expanded={expanded === idx}
            onToggle={() => setExpanded(expanded === idx ? null : idx)}
          />
        ))}
      </div>
    </section>
  );
}

function GapCard({ gap, gender, config, expanded, onToggle }) {
  const urls = buildShopUrls(gap, gender, config);
  const badgeColor = gap.gap_type === "mismatch" ? "bg-amber-100 text-amber-700" : "bg-[#6B7A54]/15 text-[#6B7A54]";
  const badgeLabel = gap.gap_type === "mismatch" ? "Better fit" : "Missing";

  const sites = [
    { key: "myntra", label: "Myntra", url: urls.myntra, primary: true },
    { key: "ajio", label: "Ajio", url: urls.ajio },
    { key: "amazon", label: "Amazon", url: urls.amazon },
  ];

  return (
    <div
      data-testid={`gap-card-${gap.category}`}
      className="rounded-2xl border border-stone-200 bg-white/80 backdrop-blur-sm p-4 shadow-[0_2px_8px_rgba(28,25,23,0.04)]"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[9px] font-[Manrope] font-bold tracking-[0.25em] uppercase px-2 py-0.5 rounded-full ${badgeColor}`}>
              {badgeLabel}
            </span>
            {gap.color && (
              <span className="text-[9px] font-[Manrope] tracking-[0.15em] uppercase text-stone-500 px-2 py-0.5 rounded-full border border-stone-200">
                {gap.color}
              </span>
            )}
            {gap.fabric && (
              <span className="text-[9px] font-[Manrope] tracking-[0.15em] uppercase text-stone-500 px-2 py-0.5 rounded-full border border-stone-200">
                {gap.fabric}
              </span>
            )}
          </div>
          <div className="font-[Cormorant_Garamond] text-xl leading-tight text-stone-900">
            {gap.name}
          </div>
          <div className="text-xs font-[Manrope] text-stone-500 mt-1">
            ₹{gap.price_min.toLocaleString("en-IN")} – ₹{gap.price_max.toLocaleString("en-IN")}
          </div>
        </div>
      </div>

      <button
        data-testid={`gap-why-toggle-${gap.category}`}
        onClick={onToggle}
        className="mt-2 flex items-center gap-1 text-[11px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-[#6B7A54] hover:opacity-80 transition-opacity"
      >
        Why this suggestion?
        <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="mt-2 text-sm font-[Manrope] text-stone-700 leading-relaxed italic">
          {gap.reason}
        </div>
      )}

      <div className="mt-3 space-y-2">
        <a
          data-testid={`gap-shop-btn-${gap.category}-myntra`}
          href={sites[0].url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackShopClick(gap, "myntra")}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#6B7A54] text-white text-sm font-[Manrope] font-medium py-2.5 hover:bg-[#5b6a48] active:scale-[0.98] transition-all"
        >
          Find it on Myntra
          <ExternalLink size={14} strokeWidth={1.75} />
        </a>
        <div className="grid grid-cols-2 gap-2">
          <a
            data-testid={`gap-shop-btn-${gap.category}-ajio`}
            href={sites[1].url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackShopClick(gap, "ajio")}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#6B7A54]/40 text-[#6B7A54] text-xs font-[Manrope] font-medium py-2 hover:bg-[#6B7A54]/5 active:scale-[0.98] transition-all"
          >
            Ajio
            <ExternalLink size={11} strokeWidth={1.75} />
          </a>
          <a
            data-testid={`gap-shop-btn-${gap.category}-amazon`}
            href={sites[2].url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackShopClick(gap, "amazon")}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#6B7A54]/40 text-[#6B7A54] text-xs font-[Manrope] font-medium py-2 hover:bg-[#6B7A54]/5 active:scale-[0.98] transition-all"
          >
            Amazon
            <ExternalLink size={11} strokeWidth={1.75} />
          </a>
        </div>
      </div>
    </div>
  );
}
